import {
    bexHttpClient
} from '../../clients';
import {
    UserSignupRequest,
    UserLoginRequest,
    AdminLoginRequest,
    IsUserAuthResponse,
    GetUserResponse,
    GetUserByEmailResponse,
    UserSignupResponse,
    UserLoginResponse,
    AdminLoginResponse,
    LogoutResponse,
    ResetPasswordWithTokenResponse,
    ForgotPasswordResponse,
    CheckResetPasswordTokenResponse,
    ResetPasswordResponse,
    ResetAdminPasswordResponse,
    VerifyEmailResponse,
    SendEmailResponse,
    VerifyInvitationTokenResponse
} from '../bexService.types';

/**
 * Service class for performing authentication operations.
 */
class AuthService {
    /**
     * Fetches the authenticated user.
     * @returns {Promise<IsUserAuthResponse>} The response from the server indicating the user's authentication status.
     */
    async getUser(): Promise<IsUserAuthResponse> {
        const response = await bexHttpClient.get('/auth/is-user-auth');
        return response;
    }

    /**
     * Fetches the authenticated user.
     * @returns {Promise<GetUserResponse>} The response from the server indicating the user's authentication status.
     */
    async getUserObject(userId: string): Promise<GetUserResponse> {
        const response = await bexHttpClient.get(`/auth/get-user/${userId}`);
        return response;
    }

    /**
     * Signs up a new user with the provided data.
     * @param {UserSignupRequest} data - The signup data including name, email, and password.
     * @returns {Promise<UserSignupResponse>} The response from the server after attempting to create a new user.
     */
    async signup(data: UserSignupRequest): Promise<UserSignupResponse> {
        const response = await bexHttpClient.post('/auth/signup', data);
        return response;
    }

    /**
     * Logs in a user with the provided credentials.
     * @param {UserLoginRequest} data - The login data including email and password.
     * @returns {Promise<UserLoginResponse>} The response from the server after attempting to log in the user.
     */
    async login(data: UserLoginRequest): Promise<UserLoginResponse> {
        const response = await bexHttpClient.post('/auth/login', data);
        return response;
    }

    /**
     * Logs in an admin user with the provided credentials.
     * @param {AdminLoginRequest} data - The admin login data including email and password.
     * @returns {Promise<AdminLoginResponse>} The response from the server after attempting to log in the admin user.
     */
    async adminLogin(data: AdminLoginRequest): Promise<AdminLoginResponse> {
        const response = await bexHttpClient.post('/auth/admin-login', data);
        return response;
    }

    /**
     * Logs out the current user.
     * @returns {Promise<LogoutResponse>} The response from the server after attempting to log out the user.
     */
    async logout(): Promise<LogoutResponse> {
        const response = await bexHttpClient.post('/auth/logout');
        return response;
    }

    /**
     * Resets a user's password using a token.
     * @param {string} token - The token for resetting the password.
     * @param {string} newPassword - The new password to set.
     * @returns {Promise<ResetPasswordWithTokenResponse>} The response from the server after attempting to reset the password.
     */
    async resetPasswordWithToken(token: string, newPassword: string): Promise<ResetPasswordWithTokenResponse> {

        const response = await bexHttpClient.post(`/auth/reset-password-with-token/${token}`, {
            newPassword: newPassword
        });
        return response;
    }

    /**
     * Initiates the forgot password process for a given email.
     * @param {string} email - The email address to send the password reset link to.
     * @returns {Promise<ForgotPasswordResponse>} The response from the server after attempting to initiate the forgot password process.
     */
    async forgotPassword(email: string): Promise<ForgotPasswordResponse> {
        const response = await bexHttpClient.post('/auth/forgot-password', {
            email: email
        });
        return response;
    }

    /**
     * Checks the validity of a password reset token.
     * @param {string} token - The token to check.
     * @returns {Promise<CheckResetPasswordTokenResponse>} The response from the server indicating whether the token is valid.
     */
    async checkResetPasswordToken(token: string): Promise<CheckResetPasswordTokenResponse> {
        const response = await bexHttpClient.get(`/auth/check-reset-password-token/${token}`);
        return response;
    }

    /**
     * Resets a user's password with the provided email.
     * @param {string} email - The email address of the user whose password is to be reset.
     * @returns {Promise<ResetPasswordResponse>} The response from the server after attempting to reset the password.
     */
    async resetPassword(email: string): Promise<ResetPasswordResponse> {
        const response = await bexHttpClient.post(`/auth/reset-password/${email}`);
        return response;
    }

    /**
     * Resets an admin's password with the provided email and new password.
     * @param {string} email - The email address of the admin whose password is to be reset.
     * @param {string} password - The new password to set.
     * @returns {Promise<ResetAdminPasswordResponse>} The response from the server after attempting to reset the admin's password.
     */
    async resetAdminPassword(email: string, password: string): Promise<ResetAdminPasswordResponse> {
        const response = await bexHttpClient.post(`/auth/reset-admin-password/${email}`, {
            password
        });
        return response;
    }

    /**
     * Verifies a user's account with the provided token.
     * @param {string} token - The token to verify the account.
     * @returns {Promise<VerifyEmailResponse>} The response from the server after attempting to verify the account.
     */
    async verify(token: string): Promise<VerifyEmailResponse> {
        const response = await bexHttpClient.get('/auth/verify', {
            params: {
                token: token
            }
        });
        return response;
    }

    /**
     * Sends an email to the provided email address.
     * @param {string} email - The email address to send the email to.
     * @returns {Promise<SendEmailResponse>} The response from the server after attempting to send the email.
     */
    async sendEmail(email: string): Promise<SendEmailResponse> {
        const response = await bexHttpClient.post(`/auth/send-email/${email}`);
        return response;
    }

    /**
     * Gets a user by email address.
     * @param {string} email - The email address to get the user by.
     * @returns {Promise<GetUserByEmailResponse>} The response from the server after attempting to get the user.
     */
    async getUserByEmail(email: string): Promise<GetUserByEmailResponse> {
        const response = await bexHttpClient.get(`/auth/get-user-by-email/${email}`);
        return response;
    }

    /**
     * verifies invitation token.
     * @param {string} token - The invitation token to verify.
     * @returns {Promise<VerifyInvitationTokenResponse>} The response from the server after attempting to verify the invitation token.
     */
    async verifyInvitationToken(token: string): Promise<VerifyInvitationTokenResponse> {
        const response = await bexHttpClient.get(`/auth/verify-invitation-token/${token}`);
        return response;
    }

    async updateExpiredPassword(data: { email: string; oldPassword: string; newPassword: string }): Promise<any> {
        const response = await bexHttpClient.post('/auth/update-expired-password', data);
        return response;
    }
}

const authService = new AuthService();
export { authService };